import React, { useState, useEffect } from "react";
import { X, Shield, Users } from "lucide-react";
import useFinance from "../../../hooks/useFinance";
import PermissionBadge from "../Common/PermissionBadge";
import LoadingSpinner from "../Common/LoadingSpinner";

export default function TablePermissions({
  isOpen,
  onClose,
  tableId,
  tableName,
}) {
  const { fetchTablePermissions } = useFinance();

  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    if (isOpen) {
      loadPermissions();
    }
  }, [isOpen]);

  const loadPermissions = async () => {
    setLoading(true);
    try {
      const result = await fetchTablePermissions(tableId);
      if (result.ok) {
        setPermissions(result.permissions);
      }
    } catch (error) {
      console.error("Failed to load permissions:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Group permissions by type
  const groupedPermissions = {
    owner: permissions.filter((p) => p.permission_type === "owner"),
    admin: permissions.filter((p) => p.permission_type === "admin"),
    edit: permissions.filter((p) => p.permission_type === "edit"),
    view: permissions.filter((p) => p.permission_type === "view"),
  };

  const sections = [
    { key: "owner", title: "소유자", color: "blue" },
    { key: "admin", title: "관리자", color: "purple" },
    { key: "edit", title: "편집자", color: "green" },
    { key: "view", title: "조회자", color: "gray" },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="w-6 h-6" />
              <div>
                <h2 className="text-xl font-bold">테이블 권한 현황</h2>
                <p className="text-sm text-purple-100 mt-1">{tableName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <div className="space-y-6">
              {sections.map(({ key, title, color }) => {
                const users = groupedPermissions[key];
                if (users.length === 0) return null;

                return (
                  <div key={key} className="space-y-3">
                    <div className="flex items-center gap-2">
                      <Users className={`w-5 h-5 text-${color}-600`} />
                      <h3 className="text-lg font-semibold text-gray-900">
                        {title}
                      </h3>
                      <span className="text-sm text-gray-500">
                        ({users.length}명)
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {users.map((user) => (
                        <div
                          key={user.user_id}
                          className={`border rounded-lg p-4 ${
                            user.is_me
                              ? "border-purple-300 bg-purple-50"
                              : "border-gray-200"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full flex items-center justify-center">
                                <span className="text-white font-medium">
                                  {user.user_name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <h4 className="font-medium text-gray-900">
                                  {user.user_name}
                                  {user.is_me && (
                                    <span className="ml-2 text-sm text-purple-600">
                                      (나)
                                    </span>
                                  )}
                                </h4>
                                <p className="text-xs text-gray-500">
                                  ID: {user.user_id}
                                </p>
                              </div>
                            </div>
                            <PermissionBadge
                              type="table"
                              level={user.permission_type}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}

              {permissions.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>권한 정보가 없습니다.</p>
                </div>
              )}

              {/* Summary */}
              <div className="mt-8 bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  권한 요약
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">전체 사용자:</span>
                    <span className="ml-2 font-medium text-gray-900">
                      {permissions.length}명
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">소유자:</span>
                    <span className="ml-2 font-medium text-blue-600">
                      {groupedPermissions.owner.length}명
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">관리자:</span>
                    <span className="ml-2 font-medium text-purple-600">
                      {groupedPermissions.admin.length}명
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">편집/조회:</span>
                    <span className="ml-2 font-medium text-gray-600">
                      {groupedPermissions.edit.length +
                        groupedPermissions.view.length}
                      명
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
